import {
  Avatar,
  Button,
  Col,
  message,
  Row,
  Space,
  Table,
  Tag,
  Modal,
  Tooltip,
} from "antd";
import {
  CloudDownloadOutlined,
  DeleteFilled,
  InfoCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useContext, useEffect, useMemo, useState } from "react";
import { OrgEmployeeType } from "./team-types";
import { ColumnsType } from "antd/es/table";
import { PageContainer } from "@ant-design/pro-layout";
import { EmployeeOperation } from "../../serviceoperation/EmployeeOperation";
import { CompanyContext } from "../../contexts/company-context";
import AddEmployeeForm from "./modal/addEmployee";
import { EmployeeDetails } from "../../serviceoperation/entity/EmployeeOperationEntity";
import "./team.css";
import { AuthenticationContext } from "../../contexts/authentication-context";
import EmployeeDetailsDrawer from "../employees/EmployeeDetailsDrawer";
import { syncStatusConfig } from "../employees/helpers/syncStatusConfig";
import { downloadCSV } from "../../Util/csvExport";
import dayjs from "dayjs";

const Team = () => {
  const userInfo = useContext(AuthenticationContext)?.userInfo;
  const [windowWidth, setWindowWidth] = useState(0);
  const isSmallScreen = windowWidth <= 576;
  const { selectedCompany } = useContext(CompanyContext);
  const [dataLoading, setDataLoading] = useState<boolean>(false);
  const [totalResult, setTotalResult] = useState<number>(0);
  const [deFaultPageSize] = useState<number>(25);
  const [updateEmpList, SetupdateEmpList] = useState(false);

  const [OpenAddUser, setOpenAddUser] = useState(false);

  // List of employee for a given logged in user.
  // This will contain a data regardless of who is logged in
  // as a logged in user will be in this list.
  const [employee, setEmployee] = useState<OrgEmployeeType[]>([]);
  const [selectedEmp, setselectedEmp] = useState<EmployeeDetails>();
  const employeeOps = useMemo(() => new EmployeeOperation(), []);

  useEffect(() => {
    setWindowWidth(window.innerWidth);
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchEmployeeData = async (
    corpId: number,
    page: number,
    pageSize: number,
  ) => {
    setDataLoading(true);
    const response = await employeeOps.getEmployeeAsync(
      corpId,
      page,
      pageSize,
      true,
    );
    setDataLoading(false);
    return response;
  };

  const fetchListofEmployee = async (
    corpId: number,
    page: number,
    pageSize: number,
  ) => {
    const response = await fetchEmployeeData(corpId, page, pageSize);
    setTotalResult(response.rowCount);
    return response.results.map((s) => ({
      key: s.employeeId,
      name: `${s.firstName} ${s.lastName}`,
      email: s.email,
      title: s.title,
      status:
        s.invitePending && s.isActive
          ? "Pending Invitation"
          : s.isActive
          ? "Active"
          : "Terminated",
      isSyncEnabled: s.isSyncEnabled ?? false,
      syncStatus: s.syncStatus,
    }));
  };
  // Using this effect call to fetch the Employee List
  useEffect(() => {
    fetchListofEmployee(
      selectedCompany?.corporationId as number,
      1,
      deFaultPageSize,
    ).then(setEmployee);
  }, [employeeOps, selectedCompany, updateEmpList]);

  const columns: ColumnsType<OrgEmployeeType> = [
    {
      title: "Name",
      dataIndex: "name",
      width: "30%",
      render(text, record) {
        return (
          <Space>
            <Avatar
              src={
                <img
                  src="https://gw.alipayobjects.com/zos/antfincdn/efFD%24IOql2/weixintupian_20170331104822.jpg"
                  alt="avatar"
                />
              }
            />
            <div>
              {text}
              <br />
              {record.title}
            </div>
          </Space>
        );
      },
      filters: employee.map((s) => ({
        text: s.name,
        value: s.name,
      })),
      filterSearch: true,
      onFilter: (value, record) => record.name === value,
    },
    {
      title: "Status",
      dataIndex: "status",
      width: "20%",
      render: (_, record) => {
        const { isSyncEnabled, syncStatus, status } = record;

        const syncConfig = syncStatus
          ? syncStatusConfig("")[syncStatus]
          : undefined;

        return (
          <>
            {isSyncEnabled && syncConfig && (
              <Tooltip title={syncConfig.tooltip}>
                <span style={{ marginRight: 8 }}>{syncConfig.icon}</span>
              </Tooltip>
            )}

            <Tag
              color={
                status === "Active"
                  ? "green"
                  : status === "Pending Invitation"
                  ? "blue"
                  : "volcano"
              }
            >
              {status}
            </Tag>
          </>
        );
      },
      filters: [
        {
          text: "Active",
          value: "Active",
        },
        {
          text: "Terminated",
          value: "Terminated",
        },
        {
          text: "Pending Invitation",
          value: "Pending Invitation",
        },
      ],
      filterSearch: true,
      onFilter: (value, record) => record.status === value,
      responsive: ["xs", "sm"],
    },
    {
      title: "Email",
      dataIndex: "email",
      width: "30%",
      onFilter: (value, record) => record.status === value,
      responsive: ["sm"],
    },
    {
      title: "Action",
      render: (_, record) => (
        <>
          <Space size="middle">
            {record.status !== "Requested" ? (
              <>
                {record.status !== "Terminated" &&
                userInfo?.employeeCorporation?.find(
                  (y) => y.corporationId === selectedCompany?.corporationId,
                )?.employeeId !== record.key ? (
                  <DeleteFilled
                    style={{ color: "red" }}
                    onClick={() => {
                      deleteEmployee(record.key.toString(), record.name);
                    }}
                  />
                ) : null}
                {/*<EditOutlined
                  onClick={() => {
                    showDrawer(record.key);
                  }}
                />*/}
                <InfoCircleOutlined
                  onClick={() => {
                    showDrawer(record.key);
                  }}
                />
              </>
            ) : null}
          </Space>
        </>
      ),
      responsive: ["sm"],
      //width: isSmallScreen ? "50%" : "20%",
    },
  ];

  const { confirm } = Modal;
  const deleteEmployee = (empId: string, empName: string) => {
    confirm({
      title: `Are you sure you want to terminate ${empName}?`,
      async onOk() {
        try {
          const result = await employeeOps.deleteEmployeeAsync(
            selectedCompany?.corporationId as number,
            empId,
          );
          if (result) {
            message.success("Successfully terminated employee.");
            return;
          } else {
            message.error("An error occured while processing your request.");
          }
        } catch (e) {
          message.error("An error occured while processing your request.");
        }
      },
      onCancel() {
        return;
      },
    });

    /*const empOps = async () => {
      const result = await employeeOps.deleteEmployeeAsync(1, empId);
      if (result) {
        message.success("Successfully terminated employee.");
      } else {
        message.error("An error occured while processing your request.");
      }
    };
    empOps();*/
  };

  const onUserCreate = () => {
    //console.log("Received values of form: ", values);
    setOpenAddUser(false);
    // This is just so that i can trigger
    // UserEffect call to get the latest schedule.
    SetupdateEmpList(!updateEmpList);

    //Show Alert successfully inserted schedule???
    message.success("Successfully sent the invitation.");
  };

  const onUserUpdated = (updatedUser: EmployeeDetails) => {
    setEmployee((prev: OrgEmployeeType[]) =>
      prev.map((emp) => {
        if (emp.key !== updatedUser.employeeId) return emp;

        // Map fields manually so types stay consistent
        return {
          ...emp,
          name: `${updatedUser.firstName} ${updatedUser.lastName}`,
          email: updatedUser.email ?? emp.email,
          phone: updatedUser.phone,
          // Add any other fields that overlap
        };
      }),
    );

    setOpen(false);
  };

  const onUserDelete = (employeeId: string) => {
    setEmployee((prev) => prev.filter((emp) => emp.key !== employeeId));
    setOpen(false);
  };

  const [open, setOpen] = useState(false);

  const showDrawer = (empId: React.Key) => {
    setselectedEmp(undefined);
    // Call the EmployeeByID Endpoit...
    const getEmployee = async () => {
      const employees = await employeeOps.getEmployeeByIdAsync(
        selectedCompany?.corporationId as number,
        empId.toString(),
      );
      setselectedEmp(employees);
    };
    getEmployee();
    setOpen(true);
  };

  const onPageChange = (page: number) => {
    fetchListofEmployee(
      selectedCompany?.corporationId as number,
      page,
      deFaultPageSize,
    ).then(setEmployee);
  };

  const generateTeamCSV = (employees: EmployeeDetails[]): string => {
    const header = [
      "Name",
      "Title",
      "Email",
      "Status",
      "Sync Enabled",
      "Sync Status",
    ];
    const rows = [header];

    employees.forEach((emp) => {
      const status =
        emp.invitePending && emp.isActive
          ? "Pending Invitation"
          : emp.isActive
          ? "Active"
          : "Terminated";

      rows.push([
        `${emp.firstName ?? ""} ${emp.lastName ?? ""}`.trim(),
        emp.title ?? "",
        emp.email ?? "",
        status,
        emp.isSyncEnabled ? "Yes" : "No",
        emp.syncStatus ?? "",
      ]);
    });

    return rows
      .map((row) => row.map((cell) => `"${String(cell ?? "")}"`).join(","))
      .join("\n");
  };

  const handleDownloadCsv = async () => {
    if (!selectedCompany?.corporationId) {
      message.error("Please select a company first.");
      return;
    }

    try {
      const response = await fetchEmployeeData(
        selectedCompany.corporationId,
        1,
        totalResult > 0 ? totalResult : deFaultPageSize,
      );

      if (!response?.results?.length) {
        message.info("No team members found to export.");
        return;
      }

      const csvContent = generateTeamCSV(response.results);
      const filename = `team-members-${dayjs().format("YYYY-MM-DD")}.csv`;
      downloadCSV(csvContent, filename);
      message.success("Team members exported successfully!");
    } catch (error) {
      message.error("Failed to export team members.");
    }
  };

  return (
    <PageContainer
      extra={[
        <Button
          key="2"
          icon={<CloudDownloadOutlined />}
          onClick={handleDownloadCsv}
        >
          Download CSV
        </Button>,
        <Button
          key="1"
          icon={<PlusOutlined />}
          onClick={() => {
            setOpenAddUser(true);
          }}
          type="primary"
        >
          Add User
        </Button>,
      ]}
      title="Team members"
      subTitle="Manage team members across your organization. "
    >
      <Row gutter={16} style={{ marginTop: "32px" }}>
        <Col span={24}>
          <Table
            expandable={
              isSmallScreen
                ? {
                    expandedRowRender: (record) => (
                      <>
                        Eamil: {record.email}
                        <br />
                        <br />
                        <Space size="middle">
                          {record.status !== "Requested" ? (
                            <>
                              {record.status !== "Terminated" &&
                              userInfo?.employeeCorporation?.find(
                                (y) =>
                                  y.corporationId ===
                                  selectedCompany?.corporationId,
                              )?.employeeId !== record.key ? (
                                <DeleteFilled
                                  style={{ color: "red" }}
                                  onClick={() => {
                                    deleteEmployee(
                                      record.key.toString(),
                                      record.name,
                                    );
                                  }}
                                />
                              ) : null}
                              {/*<EditOutlined
                                onClick={() => {
                                  showDrawer(record.key);
                                }}
                              />*/}
                              <InfoCircleOutlined
                                onClick={() => {
                                  showDrawer(record.key);
                                }}
                              />
                            </>
                          ) : null}
                        </Space>
                      </>
                    ),
                    rowExpandable: (record) => record.name !== "Not Expandable",
                  }
                : undefined
            }
            style={{ whiteSpace: "pre" }}
            columns={columns}
            dataSource={employee}
            loading={dataLoading}
            pagination={{
              pageSize: deFaultPageSize,
              total: totalResult,
              onChange: (page) => onPageChange(page),
            }}
            expandRowByClick
          />
        </Col>
      </Row>
      <AddEmployeeForm
        open={OpenAddUser}
        onCreate={onUserCreate}
        onCancel={() => {
          setOpenAddUser(false);
        }}
      />
      <EmployeeDetailsDrawer
        open={open}
        employee={selectedEmp ?? null}
        onClose={() => setOpen(false)}
        onUpdate={onUserUpdated}
        onDelete={onUserDelete}
      />
      {/*
      
        <Link to={{ pathname: `/profile/${selectedEmp?.employeeId}` }}>
          <Button type="primary" onClick={() => <ProfileLayout />}>
            View Profile
          </Button>
        </Link>
      
      */}
    </PageContainer>
  );
};

export default Team;
